import * as React from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import { FormikRadioGroup } from "../../../../UI/FormMUI/Components/indexExperiment";
import DatePicker from "react-datepicker";
import { FormControlLabel, Grid, ListItemText, Radio } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GroupsRoundedIcon from "@mui/icons-material/Groups";
import TitleRoundedIcon from "@mui/icons-material/TitleRounded";
import AssignmentIndRoundedIcon from "@mui/icons-material/AssignmentIndRounded";
import "./UI/index.css";
// import FormGroup from '@mui/material/FormGroup';
// import FormControlLabel from '@mui/material/FormControlLabel';
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import CloudRoundedIcon from "@mui/icons-material/CloudRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import { setHours, setMinutes } from "date-fns";
import { isNull, isObject } from "lodash";
import {
  FormikAutoCompleteNew,
  FormikText,
} from "components/UI/FormMUI/Components/indexTypesctipt";
import { fetchMeetingPacks } from "./EditEvent";

interface ListFormProps {
  initialStartDate: Date;
  initialEndDate: Date;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  groups: Array<{ id: number; name: string }>;
  initialValues: InitialValueType;
  values: InitialValueType;
  groupUsers: { [n: number]: Array<any> };
  isLocked: boolean;
  setFieldTouched: (
    field: string,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => void;
}

type InitialValueType = {
  title: string;
  startDate: string;
  endDate: string;
  group: number;
  invitees: number[];
  type: string;
  venue: string;
  meetingPack: number;
};
type AutocompleteOptionsType = {
  id: number;
  name: string;
};

const ListForm = ({
  initialStartDate,
  initialEndDate,
  setFieldValue,
  groups,
  initialValues,
  values,
  groupUsers,
  isLocked,
  setFieldTouched,
}: ListFormProps) => {
  // =============================== | STATE | =============================== //
  const [inviteesIsLoading, setInviteesIsLoading] = React.useState(true);
  const [meetingPacksIsLoading, setMeetingPackIsLoading] = React.useState(true);
  const [meetingPacks, setMeetingPacks] = React.useState<
    AutocompleteOptionsType[]
  >([]);
  // console.log(initialValues.title, 'INTIAL VALUES')
  // console.log(values.title, 'VALUES')

  // =============================== | PROPS | =============================== //
  const { group, meetingPack } = values;

  // =============================== | MEMO | =============================== //
  const invitees = React.useMemo(() => {
    if (isObject(groupUsers) && Array.isArray(groupUsers[group])) {
      const users = groupUsers[group].map((user) => ({
        id: user.id,
        name: user.name,
      }));
      setInviteesIsLoading(false);
      return users;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupUsers, group]);

  // =============================== | REFS | =============================== //
  const startDate = React.useRef<Date | null>(initialStartDate);
  const endDate = React.useRef<Date | null>(initialEndDate);
  const firstLoad = React.useRef(0);
  // =============================== | EFFECTS: DATES | =============================== //

  React.useEffect(() => {
    if (
      !isNull(startDate.current) &&
      !isNull(endDate.current) &&
      startDate.current > endDate.current
    ) {
      endDate.current = null;
      setFieldValue("endDate", null);
    } else if (!startDate.current) {
      endDate.current = null;
      setFieldValue("endDate", null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate.current]);

  // =============================== | EFFECTS: SET FIELD VALUES | =============================== //
  React.useEffect(() => {
    if (
      firstLoad.current === 1 &&
      isObject(groupUsers) &&
      Array.isArray(groupUsers[group])
    ) {
      setFieldValue(
        "invitees",
        groupUsers[group].map((user) => user.id)
      );
      !isNull(meetingPack) && setFieldValue("meetingPack", null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  // =============================== | EFFECTS: FETCH MEETING PACKS | =============================== //
  React.useEffect(() => {
    firstLoad.current = 1;
    fetchMeetingPacks(group)
      .then((res) => {
        if ("data" in res && Array.isArray(res.data)) {
          const { data } = res;
          setMeetingPacks(() =>
            data.map((pack) => ({ id: pack.id, name: pack.name }))
          );
          setMeetingPackIsLoading(false);
        } else {
          setMeetingPackIsLoading(false);
        }
      })
      .catch((e) => {
        console.error(e);
        setMeetingPackIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);
  React.useEffect(() => {
    setMeetingPackIsLoading(true);
  }, [group]);

  return (
    <List
      sx={{ width: "100%", maxWidth: "100%", bgcolor: "background.paper" }}
      aria-labelledby="nested-list-suheader"
    >
      <ListItem sx={{ alignItems: "flex-start" }}>
        <ListItemIcon sx={{ mt: 2 }}>
          <TitleRoundedIcon fontSize="large" />
        </ListItemIcon>
        <FormikText
          variant="standard"
          name="title"
          sx={{ width: "100%" }}
          label="Title"
          disabled={isLocked}
          setFieldValue={setFieldValue}
          id="Title"
        />
      </ListItem>
      <ListItem sx={{ alignItems: "flex-start" }}>
        <ListItemIcon sx={{ mt: 2 }}>
          <CalendarMonthIcon fontSize="large" />
        </ListItemIcon>
        <ListItemText>
          <Grid container direction="row" spacing={3}>
            <Grid item xs={6}>
              <DatePicker
                selected={startDate.current}
                // @ts-expect-error
                value={startDate.current ? startDate.current : undefined}
                onChange={(date) => {
                  if (endDate.current && !isNull(date)) {
                    const startDate = date.toDateString();
                    const endTime = endDate.current.toTimeString();
                    const combine = startDate + " " + endTime;
                    endDate.current = new Date(combine);
                    setFieldValue("endDate", endDate.current);
                  }
                  setFieldValue("startDate", date);
                  startDate.current = date;
                  setFieldTouched("startDate", true, true);
                }}
                minDate={new Date()}
                selectsStart
                disabled={isLocked}
                dateFormat="MMMM d, yyyy h:mm aa"
                showTimeSelect
                isClearable={!isLocked}
                name="startDate"
                customInput={
                  <FormikText
                    variant="standard"
                    label="Start Date"
                    name="startDate"
                    sx={{ width: "100%" }}
                    disabled={isLocked}
                    setFieldValue={setFieldValue}
                    id="Start"
                  />
                }
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                selected={endDate.current}
                // @ts-expect-error
                value={endDate.current ? endDate.current : undefined}
                showTimeSelect
                showTimeSelectOnly
                onChange={(date) => {
                  setFieldValue("endDate", date);
                  endDate.current = date;
                  setFieldTouched("endDate", true, true);
                }}
                disabled={!startDate.current ? true : false || isLocked}
                minTime={startDate.current ? startDate.current : undefined}
                maxTime={setHours(
                  setMinutes(startDate.current ?? new Date(), 30),
                  23
                )}
                selectsStart
                dateFormat="h:mm aa"
                isClearable={!startDate.current ? false : true && !isLocked}
                name="endDate"
                customInput={
                  <FormikText
                    variant="standard"
                    label="End Date"
                    sx={{ width: "100%" }}
                    setFieldValue={setFieldValue}
                    id="End"
                    name="endDate"
                  />
                }
              />
            </Grid>
          </Grid>
        </ListItemText>
      </ListItem>
      <ListItem sx={{ alignItems: "flex-start" }}>
        <ListItemIcon sx={{ mt: 2 }}>
          <GroupsRoundedIcon fontSize="large" />
        </ListItemIcon>
        <ListItemText>
          <FormikAutoCompleteNew
            variant="standard"
            name="group"
            sx={{ width: "100%" }}
            multiple={false}
            options={[...new Set(groups)] ?? []}
            label="Group"
            id="Group"
            setFieldValue={setFieldValue}
            disabled={isLocked}
          />
        </ListItemText>
      </ListItem>
      <ListItem sx={{ alignItems: "flex-start" }}>
        <ListItemIcon sx={{ mt: 2 }}>
          <AssignmentIndRoundedIcon fontSize="large" />
        </ListItemIcon>
        <ListItemText>
          <FormikAutoCompleteNew
            variant="standard"
            name="invitees"
            sx={{ width: "100%" }}
            multiple={true}
            options={Array.isArray(invitees) ? invitees : []}
            label="Invitees"
            id="Invitees"
            setFieldValue={setFieldValue}
            disabled={isLocked}
            loading={inviteesIsLoading}
          />
        </ListItemText>
      </ListItem>
      <ListItem sx={{ alignItems: "flex-start" }}>
        <ListItemIcon sx={{ mt: 2 }}>
          <FolderRoundedIcon fontSize="large" />
        </ListItemIcon>
        <ListItemText>
          <FormikAutoCompleteNew
            variant="standard"
            name="meetingPack"
            sx={{ width: "100%" }}
            multiple={false}
            options={[...new Set(meetingPacks)] ?? []}
            label="Meeting Pack"
            id="Pack"
            loading={meetingPacksIsLoading}
            setFieldValue={setFieldValue}
            disabled={isLocked}
          />
        </ListItemText>
      </ListItem>
      <ListItem sx={{ alignItems: "flex-start", mt: 2 }}>
        <ListItemIcon>
          <CloudRoundedIcon fontSize="large" />
        </ListItemIcon>
        <FormikRadioGroup
          name="type"
          sx={{ width: "100%" }}
          defaultValue={initialValues.type}
          setFieldValue={setFieldValue}
          values={values}
          disabled={true}
        >
          <FormControlLabel value="1" control={<Radio />} label="Physical" />
          <FormControlLabel value="2" control={<Radio />} label="Virtual" />
          <FormControlLabel value="3" control={<Radio />} label="Hybrid" />
        </FormikRadioGroup>
      </ListItem>
      <ListItem sx={{ alignItems: "flex-start" }}>
        <ListItemIcon sx={{ mt: 2 }}>
          <LocationOnRoundedIcon fontSize="large" />
        </ListItemIcon>
        <FormikText
          variant="standard"
          name="venue"
          sx={{ width: "100%" }}
          label="Venue"
          disabled={isLocked}
          setFieldValue={setFieldValue}
          id="Venue"
        />
      </ListItem>
    </List>
  );
};

export default React.memo(ListForm);
